import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFavoritesQuery } from '@/hooks/Favorites/useFavoritesQuery';
import { Favorite } from '@/modules/shared/domain/interfaces';
import Loader from '@/components/Loader';
import { useTheme } from '@/hooks/theme';
import { useModals } from '@/hooks/ModalManager';
import { FiCheck, FiLayers, FiStar, FiTrash2, FiX } from 'react-icons/fi';
import {
  BatchControls,
  BatchCountInput,
  BatchIconButton,
  Container,
  DeleteButton,
  EmptyMessage,
  FavoriteInfo,
  FavoriteItem,
  FavoriteName,
  IconContainer,
  ListContainer,
} from './styles';

interface FavoritesListModalProps {
  favoriteType: string;
  onSelect: (favorite: Favorite) => void;
  onBatchCreate?: (favorite: Favorite, count: number) => void;
}

const MAX_BATCH = 20;

const FavoritesListModal: React.FC<FavoritesListModalProps> = ({
  favoriteType,
  onSelect,
  onBatchCreate,
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const modals = useModals();
  const { favorites, isLoading, deleteFavorite } = useFavoritesQuery({
    favoriteType,
    screen: 'web',
  });

  const [batchState, setBatchState] = React.useState<{
    favoriteId: string;
    count: number;
  } | null>(null);

  const handleDelete = React.useCallback(
    (e: React.MouseEvent, favorite: Favorite) => {
      e.stopPropagation();
      modals.confirm({
        title: t('common.attention', 'Atenção'),
        body: t('favorites.confirm_delete', 'Deseja mesmo excluir este favorito?'),
        onConfirm() {
          deleteFavorite(favorite.id);
        },
      });
    },
    [deleteFavorite, modals, t],
  );

  const handleBatchClick = React.useCallback((e: React.MouseEvent, favorite: Favorite) => {
    e.stopPropagation();
    setBatchState({ favoriteId: favorite.id, count: 1 });
  }, []);

  const handleBatchConfirm = React.useCallback(
    (e: React.MouseEvent, favorite: Favorite) => {
      e.stopPropagation();
      if (batchState && onBatchCreate) {
        onBatchCreate(favorite, batchState.count);
      }
      setBatchState(null);
    },
    [batchState, onBatchCreate],
  );

  const handleBatchCancel = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setBatchState(null);
  }, []);

  const handleCountChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Math.min(MAX_BATCH, Math.max(1, parseInt(e.target.value) || 1));
    setBatchState((prev) => (prev ? { ...prev, count: val } : null));
  }, []);

  if (isLoading) {
    return (
      <Container style={{ alignItems: 'center', justifyContent: 'center', minHeight: 150 }}>
        <Loader loadColor={colors.primary} />
      </Container>
    );
  }

  return (
    <Container>
      {favorites.length === 0 ? (
        <EmptyMessage>{t('favorites.no_favorites', 'Nenhum favorito encontrado')}</EmptyMessage>
      ) : (
        <ListContainer className="scrollShadow">
          {favorites.map((favorite) => {
            const isBatchMode = batchState?.favoriteId === favorite.id;
            return (
              <FavoriteItem
                key={favorite.id}
                onClick={() => !isBatchMode && onSelect(favorite)}
                style={{ cursor: isBatchMode ? 'default' : 'pointer' }}
              >
                <IconContainer>
                  <FiStar size={18} fill={colors.primary} />
                </IconContainer>
                <FavoriteInfo>
                  <FavoriteName>{favorite.name}</FavoriteName>
                </FavoriteInfo>

                {isBatchMode ? (
                  <BatchControls>
                    <BatchCountInput
                      type="number"
                      min={1}
                      max={MAX_BATCH}
                      value={batchState.count}
                      onChange={handleCountChange}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <BatchIconButton
                      style={{ color: colors.primary }}
                      onClick={(e) => handleBatchConfirm(e, favorite)}
                      title={`Criar ${batchState.count} ${batchState.count === 1 ? 'tarefa' : 'tarefas'}`}
                    >
                      <FiCheck size={16} />
                    </BatchIconButton>
                    <BatchIconButton
                      style={{ color: colors.mediumText }}
                      onClick={handleBatchCancel}
                      title={t('common.cancel', 'Cancelar')}
                    >
                      <FiX size={16} />
                    </BatchIconButton>
                  </BatchControls>
                ) : (
                  <>
                    {onBatchCreate && (
                      <BatchIconButton
                        style={{ color: colors.primary }}
                        onClick={(e) => handleBatchClick(e, favorite)}
                        title={t('favorites.batch_create', 'Criar múltiplas tarefas')}
                      >
                        <FiLayers size={16} />
                      </BatchIconButton>
                    )}
                    <DeleteButton onClick={(e) => handleDelete(e, favorite)}>
                      <FiTrash2 size={18} />
                    </DeleteButton>
                  </>
                )}
              </FavoriteItem>
            );
          })}
        </ListContainer>
      )}
    </Container>
  );
};

export default FavoritesListModal;
